/**
 * The minimum an entry needs to be described. Deliberately structural rather
 * than `StorageAuditEntry`: zone rows carry `zoneId` instead of `nodeId`/`lotId`
 * and are rendered by the same component, and the description only ever reads
 * these three fields.
 */
export type DescribableEntry = {
  eventType: string
  oldValue: unknown
  newValue: unknown
}

/**
 * Turning an audit row into a sentence a person can read.
 *
 * Pure and dependency-free so it can be unit-tested — `vitest.config.ts` only
 * picks up `.test.ts` files under `src`, in a node environment with no DOM, so
 * anything worth asserting has to live outside the component. The panel only
 * renders what this returns.
 *
 * The rule this file exists to enforce: never show raw JSON. `{"blocked":true}`
 * is not an audit trail, it is a database row on a screen. Somebody
 * reconstructing what happened to a patient's product should read words.
 */

/** What the server writes into `oldValue` / `newValue`, as far as we read it. */
type Payload = Record<string, unknown> | null

export type DescribedEvent = {
  /** Short label for the event, e.g. "Put away". */
  label: string
  /** One line of specifics, or null when the label already says everything. */
  detail: string | null
}

function asPayload(v: unknown): Payload {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null
}

function str(p: Payload, key: string): string | null {
  const v = p?.[key]
  return typeof v === 'string' && v !== '' ? v : null
}

/**
 * The last segment of a storage path — "CON-01/S1/P3" → "P3".
 *
 * Trails are read next to the thing they describe, so repeating the full
 * address on every row is noise; the leaf is what distinguishes them.
 */
export function leafOf(path: string | null): string | null {
  if (!path) return null
  const parts = path.split('/').filter(Boolean)
  return parts.length ? parts[parts.length - 1] : null
}

/**
 * Field-level diff of an `updated` event, skipping the bookkeeping columns.
 *
 * `updatedAt` changes on every write and `id`/`tenantId` never change, so
 * listing them would bury the one field somebody actually edited.
 */
const IGNORED_FIELDS = new Set(['id', 'tenantId', 'updatedAt', 'createdAt', 'path', 'depth'])

export function changedFields(oldValue: unknown, newValue: unknown): string[] {
  const before = asPayload(oldValue)
  const after = asPayload(newValue)
  if (!before || !after) return []
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  const changed: string[] = []
  for (const key of keys) {
    if (IGNORED_FIELDS.has(key)) continue
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) changed.push(key)
  }
  return changed.sort()
}

export function describeStorageAuditEvent(entry: DescribableEntry): DescribedEvent {
  const before = asPayload(entry.oldValue)
  const after = asPayload(entry.newValue)

  switch (entry.eventType) {
    case 'occupied': {
      const where = leafOf(str(after, 'path'))
      const lot = str(after, 'lotNumber')
      return {
        label: 'Put away',
        detail: [lot, where && `into ${where}`].filter(Boolean).join(' ') || null,
      }
    }
    case 'vacated': {
      const lot = str(before, 'lotNumber')
      const where = leafOf(str(before, 'path'))
      const from = [lot, where && `from ${where}`].filter(Boolean).join(' ') || null
      // A slot can empty three ways and they are NOT the same fact. "Taken out"
      // means the stock still exists somewhere; the other two mean it does not.
      // Reporting a destroyed lot as "taken out" would send someone looking for
      // material that is gone.
      switch (str(after, 'cause')) {
        case 'lot_deleted':
          return { label: 'Stock deleted', detail: from }
        case 'material_deleted':
          return {
            label: 'Stock deleted',
            detail: [from, '(material deleted)'].filter(Boolean).join(' '),
          }
        default:
          return { label: 'Taken out', detail: from }
      }
    }
    case 'created': {
      // A grid declaration also writes `created`, but with a cell count rather
      // than a node — the two read very differently and must not be conflated.
      const minted = after?.['mintedCells']
      if (typeof minted === 'number') {
        return { label: 'Layout built', detail: `${minted} slot${minted === 1 ? '' : 's'} created` }
      }
      return { label: 'Created', detail: str(after, 'code') }
    }
    case 'updated': {
      const fields = changedFields(entry.oldValue, entry.newValue)
      return {
        label: 'Edited',
        detail: fields.length ? `changed ${fields.join(', ')}` : null,
      }
    }
    case 'moved': {
      const from = leafOf(str(before, 'path'))
      const to = leafOf(str(after, 'path'))
      return {
        label: 'Moved',
        detail: from && to ? `${from} → ${to}` : null,
      }
    }
    case 'blocked':
      return { label: 'Blocked', detail: 'taken out of service' }
    case 'unblocked':
      return { label: 'Unblocked', detail: 'put back in service' }
    case 'deleted':
      return { label: 'Deleted', detail: str(before, 'code') ?? str(before, 'name') }
    case 'seeded':
      return { label: 'Seeded', detail: str(after, 'code') }
    case 'migrated':
      return { label: 'Migrated', detail: null }
    case 'storage_condition_synced':
      return {
        label: 'Condition synced',
        detail: str(after, 'providesCondition') ?? 'from its storage profile',
      }
    default:
      // An event type this build has not met. Show it rather than hide it — a
      // trail with a silent gap is worse than one with an unfamiliar word.
      return { label: entry.eventType.replace(/_/g, ' '), detail: null }
  }
}

/**
 * Who did it, as a person where we can name one.
 *
 * The row stores a Cognito `sub`; the server resolves it to a user at read
 * time and sends both. Prefer the name — "Ada Lovelace" is the answer somebody
 * reading a trail actually wants, and `94284468` is not.
 *
 * When the sub resolves to nobody — a deleted account, an actor from another
 * system — fall back to a short form of the sub rather than inventing a name or
 * hiding the row. An action by somebody since removed is exactly the kind an
 * investigator cares about, and it must still be attributable to *an* id.
 */
export function actorLabel(entry: {
  changedBy: string
  changedByActor?: { name: string | null } | null
}): string {
  const name = entry.changedByActor?.name
  if (name) return name
  return shortActor(entry.changedBy)
}

/**
 * A Cognito `sub` shortened to its first segment, for when there is no name.
 *
 * Keeps rows distinguishable without pretending to a precision we do not have.
 * The full value stays available on hover.
 */
export function shortActor(changedBy: string): string {
  if (changedBy === 'system') return 'System'
  const [first] = changedBy.split('-')
  return first && first.length >= 6 ? first : changedBy
}
